import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import usePricingConfig from '../hooks/usePricingConfig';
import AffiliateEarnings from '../components/AffiliateEarnings';
import { Gift, Coins, Clock, ShieldCheck, Copy, Check, ArrowRight, Sparkles, MessageCircle } from 'lucide-react';

/**
 * Public Affiliate / Helper Program landing page.
 *
 * Renders even for unauthenticated visitors. Lets anyone:
 *   1. Read what the program is
 *   2. See current commission tiers
 *   3. Sign up as an affiliate inline (POST /api/helper/self-register)
 *   4. Copy their sequential code (F2D-000123) and a ready-to-share signup link
 *
 * Existing helpers get a "Sign in instead" link to /helper/login.
 */
const AffiliateProgram = () => {
  const { t } = useTranslation();
  const pricing = usePricingConfig();
  const navigate = useNavigate();
  const [step, setStep] = useState('intro'); // intro | form | success
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [helper, setHelper] = useState(null); // { name, referralCode, shareLink, access_token }
  const [copied, setCopied] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      setError(t('affiliateAllFields', 'Please fill in all fields.'));
      return;
    }
    if (form.password.length < 8) {
      setError(t('affiliatePwLen', 'Password must be at least 8 characters.'));
      return;
    }
    setSubmitting(true);
    try {
      // Pass the helper's UI language so the welcome email matches what they read on the site.
      const language = (typeof window !== 'undefined' && (localStorage.getItem('i18nextLng') || navigator?.language || 'en')).slice(0, 2).toLowerCase();
      const res = await axiosInstance.post('/helper/self-register', { ...form, language });
      setHelper(res.data);
      // Persist the helper session so the dashboard works after navigation
      try {
        localStorage.setItem('helper_token', res.data.access_token);
        localStorage.setItem('helper_code', res.data.referralCode);
      } catch (_e) { /* private mode */ }
      setStep('success');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Sign-up failed.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    } catch (_e) { /* clipboard blocked */ }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-white" data-testid="affiliate-program-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-purple-600 mb-6">
          ← {t('back', 'Back')}
        </button>

        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-purple-600 text-white shadow-lg">
            <Gift className="h-6 w-6" />
          </span>
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> {t('affiliateLive', 'New: earn money for every referral')}
          </span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
          {t('partnersTitle', 'Community Partners — help your town stay connected')}
        </h1>
        <p className="text-base sm:text-lg text-gray-600 mt-3 leading-relaxed">
          {t('partnersLede', 'Are you a Town Ambassador, community group, or local champion? Introduce nearby businesses to Find-2-Day — you earn 50% of every payment they make (excluding IVA), for as long as they stay: their first subscription, every renewal, and every boost or sponsored post.')}
        </p>

        {/* Clarity banner — sets payout expectations before sign-up */}
        <div className="mt-5 inline-flex flex-wrap items-center gap-2" data-testid="affiliate-clarity-banner">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-purple-200 text-purple-800 text-xs sm:text-sm font-medium shadow-sm" data-testid="affiliate-clarity-confirmed">
            <Clock className="h-3.5 w-3.5" />
            {t('affiliateClarityConfirmed', 'Confirmed in 30 days')}
          </span>
          <span className="text-gray-300 text-xs select-none" aria-hidden="true">·</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-purple-200 text-purple-800 text-xs sm:text-sm font-medium shadow-sm" data-testid="affiliate-clarity-paid">
            <Coins className="h-3.5 w-3.5" />
            {t('affiliateClarityPaid', 'Paid from €20')}
          </span>
        </div>

        {/* Commission cards — Phase B model: 50% on boosts/sponsored, 50% on renewals */}
        <p className="text-sm text-gray-700 mt-8 mb-3" data-testid="affiliate-commission-explainer">
          {t('partnersCommissionExplainer', 'You earn 50% of everything a business you referred pays — their first subscription, every monthly or annual renewal, and every boost or sponsored post. Prices include 21% IVA; your commission is calculated on the amount excluding IVA.')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="affiliate-commission-grid">
          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('partnersBoostHeading', 'Boosts & Sponsored Posts')}</p>
            <p className="mt-2 text-3xl font-bold text-purple-700">50%</p>
            <p className="text-sm text-gray-600 mt-1">{t('partnersBoostDesc', 'of every paid boost on an event, offer or sponsored visibility purchase your business makes.')}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('partnersRenewalHeading', 'Subscriptions & Renewals')}</p>
            <p className="mt-2 text-3xl font-bold text-purple-700">50%</p>
            <p className="text-sm text-gray-600 mt-1">{t('partnersRenewalDesc', 'of the first payment and every renewal a referred business makes — for as long as they stay subscribed.')}</p>
            <p className="mt-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 inline-block" data-testid="affiliate-renewal-badge">
              {t('partnersRecurringBadge', 'Recurring — for life')}
            </p>
          </div>
        </div>

        {/* Per-plan earnings breakdown */}
        <AffiliateEarnings />

        {/* How you earn */}
        <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-200 p-5" data-testid="affiliate-how-you-earn">
          <h2 className="text-base font-semibold text-emerald-900 mb-3">
            {t('howYouEarnTitle', 'How you earn — and how you get paid')}
          </h2>
          <ul className="space-y-2 text-sm text-emerald-900">
            <li className="flex gap-2" data-testid="affiliate-how-you-earn-1">
              <span aria-hidden className="text-emerald-600">•</span>
              <span>{t('partnersPoint1', 'Businesses can join Find-2-Day with a permanent FREE basic directory listing. Optional paid subscriptions and promotional services are available. Existing founding businesses retain their originally promised 12-month free benefits.')}</span>
            </li>
            <li className="flex gap-2" data-testid="affiliate-how-you-earn-2">
              <span aria-hidden className="text-emerald-600">•</span>
              <span>{t('partnersPoint2', 'The moment they pay for a subscription, a renewal, a boost or a sponsored post — you get 50% of what they paid (excluding IVA).')}</span>
            </li>
            <li className="flex gap-2" data-testid="affiliate-how-you-earn-3">
              <span aria-hidden className="text-emerald-600">•</span>
              <span>{t('partnersPoint3', 'Every business you bring is permanently attached to you. As long as they keep paying, you keep earning 50% — for life.')}</span>
            </li>
            <li className="flex gap-2" data-testid="affiliate-how-you-earn-4">
              <span aria-hidden className="text-emerald-600">•</span>
              <span>{t('partnersPoint4', 'Commissions are held for 30 days. If a payment is refunded or successfully disputed, its commission may be reversed. Commissions already paid may be deducted from future earnings.')}</span>
            </li>
          </ul>
        </div>

        {/* How it works */}
        <div className="mt-10 space-y-4" data-testid="affiliate-howitworks">
          <h2 className="text-xl font-semibold text-gray-900">{t('howItWorks', 'How it works')}</h2>
          <ol className="space-y-3 text-sm text-gray-700">
            <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center">1</span> <span>{t('partnersStep1', 'Sign up below — it takes 30 seconds. You get your unique partner code right away (e.g. F2D-000123).')}</span></li>
            <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center">2</span> <span>{t('partnersStep2', 'Share your code with local businesses, clubs, and community groups.')}</span></li>
            <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center">3</span> <span>{t('partnersStep3', 'When a business you brought pays — their first subscription, a renewal, or a boost — you earn 50% of that payment (excluding IVA). It appears in your Partner Dashboard automatically, every time they pay.')}</span></li>
            <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center">4</span> <span>{t('partnersStep4', 'Once €20+ is approved, request payout. We pay by SEPA bank transfer, normally within 7 days.')}</span></li>
          </ol>
        </div>

        {/* Trust signals */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm" data-testid="affiliate-trust">
          <div className="flex items-start gap-2"><Coins className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" /> <span>{t('partnersTrust1', 'You only earn when a business you brought actually pays.')}</span></div>
          <div className="flex items-start gap-2"><Clock className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" /> <span>{t('partnersTrust2', '30-day cooldown protects against refunds.')}</span></div>
          <div className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" /> <span>{t('partnersTrust3', 'Reviewed and paid manually by the admin team.')}</span></div>
        </div>

        {/* Example */}
        <div className="mt-8 rounded-xl bg-purple-50 border border-purple-200 p-4 text-sm text-purple-900" data-testid="affiliate-example">
          <p>
            <strong>{t('example', 'Example')}:</strong> {pricing.affiliateExample}
          </p>
        </div>

        {/* CTA / form / success */}
        <div className="mt-10">
          {step === 'intro' && (
            <div className="space-y-3">
              <button
                onClick={() => setStep('form')}
                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-purple-600 text-white font-semibold shadow-md hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                data-testid="affiliate-cta-get-code"
              >
                {t('affiliateGetCode', 'Get my referral code')} <ArrowRight className="h-4 w-4" />
              </button>
              <p className="text-xs text-gray-500">
                {t('affiliateHaveAccount', 'Already an affiliate?')}{' '}
                <button onClick={() => navigate('/helper/login')} className="text-purple-600 hover:underline">
                  {t('signIn', 'Sign in')}
                </button>
              </p>
            </div>
          )}

          {step === 'form' && (
            <form onSubmit={submit} className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm space-y-3" data-testid="affiliate-signup-form">
              <h3 className="font-semibold text-gray-900">{t('affiliateSignUpTitle', 'Create your affiliate account')}</h3>
              <input
                type="text"
                placeholder={t('yourName', 'Your name')}
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                data-testid="affiliate-form-name"
                autoComplete="name"
              />
              <input
                type="email"
                placeholder={t('email', 'Email')}
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                data-testid="affiliate-form-email"
                autoComplete="email"
              />
              <input
                type="password"
                placeholder={t('passwordMin8', 'Password (min 8 characters)')}
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                data-testid="affiliate-form-password"
                autoComplete="new-password"
              />
              {error && <p className="text-sm text-red-600" data-testid="affiliate-form-error">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full px-4 py-2.5 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-60 transition-colors"
                data-testid="affiliate-form-submit"
              >
                {submitting ? t('creating', 'Creating...') : t('affiliateCreate', 'Create my affiliate account')}
              </button>
              <p className="text-xs text-gray-500 text-center">
                {t('affiliateHaveAccount', 'Already an affiliate?')}{' '}
                <button type="button" onClick={() => navigate('/helper/login')} className="text-purple-600 hover:underline">
                  {t('signIn', 'Sign in')}
                </button>
              </p>
            </form>
          )}

          {step === 'success' && helper && (
            <div className="bg-white rounded-2xl border-2 border-purple-200 p-5 shadow-md space-y-4" data-testid="affiliate-success">
              <div className="flex items-center gap-2 text-green-700">
                <Check className="h-5 w-5" />
                <p className="font-semibold">{t('affiliateAllSet', "You're all set, {{name}}!", { name: helper.name })}</p>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('yourReferralCode', 'Your referral code')}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 rounded-lg bg-purple-50 text-purple-700 font-mono font-bold text-lg" data-testid="affiliate-code">
                    {helper.referralCode}
                  </code>
                  <button
                    onClick={() => copy(helper.referralCode, 'code')}
                    className="px-3 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 flex items-center gap-1"
                    data-testid="affiliate-copy-code-btn"
                  >
                    {copied === 'code' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied === 'code' ? t('copied', 'Copied') : t('copy', 'Copy')}
                  </button>
                </div>
              </div>

              {/* Iter 166 — One-tap WhatsApp share. Pre-filled friendly message
                  with the affiliate's shareable signup link. Keeps copy-link
                  + copy-code controls intact below.
                  Iter 167 — WhatsApp-only link variant carries `wa=1` so signups
                  from this channel are counted separately in the dashboard.
                  Copy-link / copy-code remain untracked. */}
              {(() => {
                const waLink = helper.shareLink + (helper.shareLink.includes('?') ? '&' : '?') + 'wa=1';
                const message = t(
                  'affiliateWhatsappMessage',
                  "Hi! I just joined Find-2-Day — it's a free local app for events, offers and businesses around Almería. If you have a local business, you can list it here: {{link}}",
                  { link: waLink }
                );
                return (
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(message)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl
                               bg-[#25D366] hover:bg-[#1ebe5d] active:scale-[0.98]
                               text-white font-semibold shadow-md transition-all"
                    data-testid="affiliate-whatsapp-share-btn"
                    aria-label={t('affiliateWhatsappAria', 'Share my referral link on WhatsApp')}
                  >
                    <MessageCircle className="h-5 w-5" aria-hidden="true" />
                    {t('affiliateWhatsappCta', 'Share on WhatsApp')}
                  </a>
                );
              })()}
              <p className="-mt-2 text-xs text-gray-500 text-center" data-testid="affiliate-whatsapp-hint">
                {t('affiliateWhatsappHint', 'Opens WhatsApp with a friendly message ready to send.')}
              </p>

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('shareableLink', 'Shareable signup link')}</p>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={helper.shareLink}
                    className="flex-1 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-700 font-mono truncate"
                    data-testid="affiliate-share-link"
                  />
                  <button
                    onClick={() => copy(helper.shareLink, 'link')}
                    className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium flex items-center gap-1"
                    data-testid="affiliate-copy-link-btn"
                  >
                    {copied === 'link' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('affiliateLinkHint', 'When someone signs up via this link, your code is applied automatically.')}
                </p>
              </div>

              {/* Clarity pills — same wording as landing page + helper dashboard */}
              <div className="pt-2 border-t border-gray-100" data-testid="affiliate-success-clarity">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  {t('payoutRules', 'Payout rules')}
                </p>
                <div className="inline-flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200 text-purple-800 text-xs font-medium" data-testid="affiliate-success-clarity-confirmed">
                    <Clock className="h-3.5 w-3.5" />
                    {t('affiliateClarityConfirmed', 'Confirmed in 30 days')}
                  </span>
                  <span className="text-gray-300 text-xs select-none" aria-hidden="true">·</span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200 text-purple-800 text-xs font-medium" data-testid="affiliate-success-clarity-paid">
                    <Coins className="h-3.5 w-3.5" />
                    {t('affiliateClarityPaid', 'Paid from €20')}
                  </span>
                </div>
              </div>

              <button
                onClick={() => navigate('/helper/dashboard')}
                className="w-full mt-2 px-4 py-2.5 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 flex items-center justify-center gap-2"
                data-testid="affiliate-go-dashboard-btn"
              >
                {t('goToDashboard', 'Go to my Helper Dashboard')} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AffiliateProgram;
